import { Injectable } from '@angular/core';
import {
    addDoc,
    collection,
    collectionData,
    doc,
    Firestore,
    getDocs,
    limit,
    orderBy,
    query,
    updateDoc,
    where,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { from, Observable, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { DomainRequest, DomainRequestStatus, DomainRequestType } from '../shared/models/domain-request.model';
import { DomainType } from '../shared/models/domain.model';
import { OrganizationData } from '../shared/models/organization-data.model';

@Injectable()
export class DomainRequestService {
    private readonly collectionName = 'domainRequests';
    private readonly cargosprintSuffix = '.cargosprint.com';

    constructor(
        private readonly firestore: Firestore,
        private readonly functions: Functions
    ) {}

    public getDomainRequests(): Observable<DomainRequest[]> {
        const colRef = collection(this.firestore, this.collectionName);
        const q = query(colRef, orderBy('createdAt', 'desc'));
        return collectionData(q, { idField: 'requestId' }) as Observable<DomainRequest[]>;
    }

    public createDomainRequest(orgId: string, orgData: OrganizationData, userUid: string | null): Observable<void> {
        const domainPayload = this.buildDomainData(orgData);
        const payload: DomainRequest = {
            orgId,
            orgName: orgData.organizationName?.trim() ?? 'N/A',
            domainType: domainPayload.domainType,
            requestedDomain: domainPayload.requestedDomain,
            subdomainLabel: domainPayload.subdomainLabel,
            staticWebAppName: '',
            status: DomainRequestStatus.NEW,
            createdAt: new Date(),
            updatedAt: new Date(),
            updatedBy: userUid ?? 'system',
        };

        const colRef = collection(this.firestore, this.collectionName);
        return from(addDoc(colRef, payload)).pipe(map(() => undefined));
    }

    public updateStatus(domainRequestId: string, status: DomainRequestStatus): Observable<void> {
        const callable = httpsCallable(this.functions, 'updateDomainRequestStatus');
        return from(callable({ domainRequestId, status })).pipe(map(() => undefined));
    }

    public updateDomainRequest(
        requestId: string,
        updates: Partial<DomainRequest>,
        userUid: string | null
    ): Observable<void> {
        const payload: Partial<DomainRequest> = {
            ...updates,
            updatedAt: new Date(),
            updatedBy: userUid ?? 'system',
        };

        const docRef = doc(this.firestore, `${this.collectionName}/${requestId}`);
        return from(updateDoc(docRef, payload)).pipe(map(() => undefined));
    }

    public upsertDomainRequestByOrgId(
        orgId: string,
        orgData: OrganizationData,
        userUid: string | null
    ): Observable<void> {
        const domainPayload = this.buildDomainData(orgData);
        const colRef = collection(this.firestore, this.collectionName);
        const q = query(colRef, where('orgId', '==', orgId), limit(1));

        return from(getDocs(q)).pipe(
            switchMap((snapshot) => {
                if (snapshot.empty) {
                    return this.createDomainRequest(orgId, orgData, userUid);
                }

                const existingRequestId = snapshot.docs[0].id;
                return this.updateDomainRequest(
                    existingRequestId,
                    {
                        orgName: orgData.organizationName?.trim() ?? 'N/A',
                        domainType: domainPayload.domainType,
                        requestedDomain: domainPayload.requestedDomain,
                        subdomainLabel: domainPayload.subdomainLabel,
                        status: DomainRequestStatus.NEW,
                    },
                    userUid
                );
            })
        );
    }

    private buildDomainData(orgData: OrganizationData): {
        domainType: DomainRequestType;
        requestedDomain: string;
        subdomainLabel?: string;
    } {
        const domainType = this.mapDomainType(orgData.domainType);
        const normalizedDomain = this.normalizeDomainValue(orgData.domainValue);

        if (domainType === 'cargosprint-subdomain') {
            const subdomainLabel = this.extractSubdomainLabel(normalizedDomain);
            return {
                domainType,
                requestedDomain: subdomainLabel ? `${subdomainLabel}${this.cargosprintSuffix}` : '',
                subdomainLabel,
            };
        }

        return {
            domainType,
            requestedDomain: normalizedDomain,
        };
    }

    private mapDomainType(domainType?: DomainType): DomainRequestType {
        return domainType === 'custom' ? 'custom-domain' : 'cargosprint-subdomain';
    }

    private normalizeDomainValue(domainValue?: string): string {
        return domainValue?.trim().toLowerCase() ?? '';
    }

    private extractSubdomainLabel(normalizedDomain: string): string {
        if (!normalizedDomain) {
            return '';
        }

        if (normalizedDomain.endsWith(this.cargosprintSuffix)) {
            return normalizedDomain.replace(this.cargosprintSuffix, '');
        }

        return normalizedDomain;
    }
}
